import { IDestroyOptions, Point, Sprite } from "pixi.js";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { WinCoinsAnimation } from "../../../common/components/animations/WinCoinsAnimation";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { IAmazingWinCoinsController } from "../controllers/IAmazingWinCoinsController";
import { AmazingCoinsWinResourcePackage } from "../resources/AmazingCoinsWinResourcePackage";
import { AmazingWinCoinsRewardContainer } from "../components/AmazingWinCoinsRewardContainer";
import { gsap, Bounce } from "gsap";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { AmazingWinAction } from "../../../game/flow/impl/diceActions/AmazingWinAction";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { BaseScalableView } from "../../../common/BaseScalableView";

export class AmazingWinCoinsPopupView extends BaseScalableView<void> {
    private static readonly COINS_BACKGROUND_SCALE = 1.3;

    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly amazingCoinsWinResources: AmazingCoinsWinResourcePackage;
    private readonly amazingWinCoinsController: IAmazingWinCoinsController;
    private readonly action: AmazingWinAction;
    private fadeScreen: FadeScreen;
    private background: Sprite;
    private backLight: Sprite;
    private wonLogo: Sprite;
    private rewardContainer: AmazingWinCoinsRewardContainer;
    private coinsCollectPoint: Point;
    private coinsCollectAnimation: WinCoinsAnimation;
    private topPanel: TopPanel;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper,
        amazingCoinsWinResources: AmazingCoinsWinResourcePackage,
        canvasScaler: ICanvasScaler,
        amazingWinCoinsController: IAmazingWinCoinsController,
        action: AmazingWinAction,
        topPanel: TopPanel
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.amazingCoinsWinResources = amazingCoinsWinResources;
        this.amazingWinCoinsController = amazingWinCoinsController;
        this.action = action;
        this.topPanel = topPanel;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.show.bind(this));
    }

    public start(): void { }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.5, 0, 0.5, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.background = new Sprite(this.amazingCoinsWinResources.assets_views_amazing_win_popup_coins_background_png);
        this.background.anchor.set(0.5, 0.5);
        this.addChild(this.background);
        this.backLight = new Sprite(this.amazingCoinsWinResources.assets_views_amazing_win_popup_background_light_png);
        this.backLight.anchor.set(0.5, 0.5);
        this.addChild(this.backLight);
        this.wonLogo = new Sprite(this.amazingCoinsWinResources.assets_views_amazing_win_popup_tittle_png);
        this.wonLogo.anchor.set(0.5, 0.5);
        this.addChild(this.wonLogo)
        this.rewardContainer = new AmazingWinCoinsRewardContainer(this.amazingCoinsWinResources, this.action.diceRollRewardInCoins);
        this.addChild(this.rewardContainer);
        this.topPanel.bringToFront();
        this.coinsCollectPoint = new Point();
        this.coinsCollectAnimation = new WinCoinsAnimation(this.commonResources, this.commonResources.assets_common_ui_top_panel_coin_png, this.coinsCollectPoint, this.action.diceRollRewardInCoins);
        this.addChild(this.coinsCollectAnimation);
    }

    private async show(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.coinsCollectAnimation.visible = false;
        this.topPanel.visible = false;
        this.soundResources.assets_sounds_Collect_Screen_mp3.play();
        this.fadeScreen.fadeIn();
        animationTimeLine.fromTo(this.background.scale, { x: AmazingWinCoinsPopupView.COINS_BACKGROUND_SCALE, y: AmazingWinCoinsPopupView.COINS_BACKGROUND_SCALE }, { x: 1, y: 1, duration: 0.3 });
        animationTimeLine.addLabel("backgroundToDefault");
        animationTimeLine.fromTo(this.rewardContainer.scale, { x: 0, y: 0 }, { x: 1.1, y: 1.1, duration: 0.7, ease: Bounce.easeOut }, "<");
        animationTimeLine.fromTo(this.wonLogo.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.3, ease: "none" }, "<50%");
        animationTimeLine.fromTo(this.backLight, { alpha: 0 }, { alpha: 1, duration: 0.8, ease: "none" }, ">");
        animationTimeLine.add(this.collectCoinsAnimation.bind(this), ">");
        animationTimeLine.fromTo(this.background.scale, { x: 1, y: 1 }, { x: AmazingWinCoinsPopupView.COINS_BACKGROUND_SCALE, y: AmazingWinCoinsPopupView.COINS_BACKGROUND_SCALE, duration: 5 }, "backgroundToDefault");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private async collectCoinsAnimation(): Promise<void> {
        this.topPanel.visible = true;
        await this.topPanel.show();
        this.amazingWinCoinsController.collectCoins(this.action.diceRollRewardInCoins);
        this.coinsCollectAnimation.visible = true;
        this.soundResources.assets_sounds_Coins_Top_Bar_mp3.play();
        await this.coinsCollectAnimation.startAnimation();
        await this.hidePage();
    }

    private async hidePage(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        await animationTimeLine.to(this, { alpha: 0, duration: 0.5, ease: "power1.out" });
        animationTimeLine.kill();
        this.operationControls.complete();
    }

    private layout(width: number, height: number): void {
        this.coinsCollectAnimation.position.set(width * 0.45, height * 0.45);
        this.background.scale.set(AmazingWinCoinsPopupView.COINS_BACKGROUND_SCALE, AmazingWinCoinsPopupView.COINS_BACKGROUND_SCALE);
        this.background.position.set(width * 0.5, height * 0.5);
        this.backLight.position.set(width * 0.5, height * 0.5);
        this.wonLogo.position.set(width * 0.5, 430);
        this.rewardContainer.position.set(width * 0.5 - 80, height * 0.52);
        this.coinsCollectPoint.set(0, TopPanel.DEFAULT_POSITION_Y - this.coinsCollectAnimation.position.y);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.topPanel.bringToOriginParent();
        super.destroy(options);
    }
}
